'use client'

import * as React from 'react'
import { useLocalStorage } from '@/lib/hooks/use-local-storage'

const LOCAL_STORAGE_KEY = 'sidebar'
const DEFAULT_SIDEBAR_WIDTH = 280
const COLLAPSED_WIDTH = 50
const CHAT_LIST_WIDTH = 400
const MIN_CHAT_LIST_WIDTH = 300
const MAX_CHAT_LIST_WIDTH = typeof window !== 'undefined' ? Math.max(window.innerWidth * 0.5, 500) : 500

interface SidebarState {
    isOpen: boolean
    width: number
    isChatListOpen: boolean
    isNotificationsOpen: boolean
}

interface SidebarContext {
    isSidebarOpen: boolean
    toggleSidebar: () => void
    isLoading: boolean
    closeSidebar: () => void
    sidebarWidth: number
    setSidebarWidth: (width: number) => void
    isChatListOpen: boolean
    setChatListOpen: (open: boolean) => void
    isNotificationsOpen: boolean
    setNotificationsOpen: (open: boolean) => void
}

const SidebarContext = React.createContext<SidebarContext | undefined>(undefined)

export function useSidebar() {
    const context = React.useContext(SidebarContext)
    if (!context) {
        throw new Error('useSidebarContext must be used within a SidebarProvider')
    }
    return context
}

interface SidebarProviderProps {
    children: React.ReactNode
    defaultOpen?: boolean
}

export function SidebarProvider({ children, defaultOpen = false }: SidebarProviderProps) {
    const [isLoading, setLoading] = React.useState(true)
    const initialState: SidebarState = {
        isOpen: defaultOpen,
        width: DEFAULT_SIDEBAR_WIDTH,
        isChatListOpen: false,
        isNotificationsOpen: false
    }
    const [state, setState] = useLocalStorage<SidebarState>(LOCAL_STORAGE_KEY, initialState)

    React.useEffect(() => {
        setLoading(false)
    }, [])

    const toggleSidebar = React.useCallback(() => {
        setState({
            ...state,
            isOpen: !state.isOpen,
            width: !state.isOpen ? (state.isChatListOpen || state.isNotificationsOpen ? CHAT_LIST_WIDTH : DEFAULT_SIDEBAR_WIDTH) : COLLAPSED_WIDTH
        })
    }, [setState, state])

    const closeSidebar = React.useCallback(() => {
        setState({
            ...state,
            isOpen: false,
            width: COLLAPSED_WIDTH
        })
    }, [setState, state])

    const setSidebarWidth = React.useCallback((width: number) => {
        if (!state.isChatListOpen && !state.isNotificationsOpen) return
        
        const constrainedWidth = Math.max(
            MIN_CHAT_LIST_WIDTH,
            Math.min(width, MAX_CHAT_LIST_WIDTH)
        )
        
        setState({
            ...state,
            width: constrainedWidth
        })
    }, [setState, state])

    const setChatListOpen = React.useCallback((open: boolean) => {
        setState({
            ...state,
            isChatListOpen: open,
            isNotificationsOpen: false,
            width: open ? CHAT_LIST_WIDTH : DEFAULT_SIDEBAR_WIDTH,
            isOpen: true
        })
    }, [setState, state])

    const setNotificationsOpen = React.useCallback((open: boolean) => {
        // Notification feature has been removed - just a no-op function
    }, [])

    if (isLoading) {
        return null
    }

    return (
        <SidebarContext.Provider
            value={{
                isSidebarOpen: state.isOpen,
                toggleSidebar,
                isLoading,
                closeSidebar,
                sidebarWidth: state.width,
                setSidebarWidth,
                isChatListOpen: state.isChatListOpen,
                setChatListOpen,
                isNotificationsOpen: state.isNotificationsOpen,
                setNotificationsOpen
            }}
        >
            {children}
        </SidebarContext.Provider>
    )
}
